import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type CtaVariant = 'solid' | 'line' | 'invert'

interface CtaProps {
    href: string
    children: ReactNode
    variant?: CtaVariant
    className?: string
    cursor?: string
    external?: boolean
}

const variants: Record<CtaVariant, { root: string, fill: string, box: string }> = {
    solid: {
        root: 'bg-violet text-white hover:text-void',
        fill: 'bg-mist',
        box: 'bg-void/15'
    },
    line: {
        root: 'border border-tone-line text-tone hover:text-tone-bg',
        fill: 'bg-tone',
        box: 'bg-tone/10'
    },
    invert: {
        root: 'bg-tone text-tone-bg hover:text-white',
        fill: 'bg-violet',
        box: 'bg-tone-bg/10'
    }
}

// Square-edged call to action: a fill wipes across on hover and the arrow
// exits and re-enters its box.
export const Cta: FC<CtaProps> = ({ href, children, variant = 'solid', className, cursor, external = false }): ReactNode => {
    const style = variants[variant]
    const externalProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {}

    return (
        <Link
            href={href}
            data-cursor={cursor}
            className={cn(
                'group relative inline-flex h-14 items-center gap-5 overflow-hidden pl-6 pr-2 text-[0.9375rem] font-semibold tracking-tight transition-colors duration-500',
                style.root,
                className
            )}
            {...externalProps}
        >
            <span
                aria-hidden
                className={cn('absolute inset-0 origin-left scale-x-0 transition-transform duration-700 ease-out-expo group-hover:scale-x-100', style.fill)}
            />

            <span className='relative z-10'>
                {children}
            </span>

            <span className={cn('relative z-10 flex size-10 items-center justify-center overflow-hidden', style.box)}>
                <ArrowUpRight className='size-4 transition-transform duration-500 ease-out-expo group-hover:-translate-y-6 group-hover:translate-x-6' />
                <ArrowUpRight className='absolute size-4 -translate-x-6 translate-y-6 transition-transform duration-500 ease-out-expo group-hover:translate-x-0 group-hover:translate-y-0' />
            </span>
        </Link>
    )
}
